import axiosInstance from './axiosInstance';

export interface Leave {
  leaveId: number;
  employeeId: number;
  employeeCode: string;
  employeeName: string;
  employeeNameAr: string;
  branchId: number;
  branchName?: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  paidDays: number;
  unpaidDays: number;
  deductionAmount: number;
  isReturned: boolean;
  actualReturnDate?: string;
  returnApprovedBy?: number;
  returnApprovedByName?: string;
  returnApprovedDate?: string;
  returnNotes?: string;
  opsManagerAcknowledged: boolean;
  opsManagerId?: number;
  opsManagerName?: string;
  opsManagerDate?: string;
  status: string;
  approvedBy?: number;
  approvedByName?: string;
  approvedDate?: string;
  isApplied: boolean;
  appliedInPayrollId?: number;
  reason?: string;
  notes?: string;
  attachmentPath?: string;
  attachmentPath2?: string;
  attachmentPath3?: string;
  createdDate: string;
  createdBy?: number;
  createdByName?: string;
  annualBalance?: LeaveBalance;
}

export interface LeaveBalance {
  employeeId: number;
  employeeName: string;
  hireDate: string;
  monthsOfService: number;
  totalEntitlement: number;
  usedDays: number;
  remainingDays: number;
  sickUsed: number;
  sickRemaining: number;
  hajjUsed: boolean;
}

export interface CreateLeaveDto {
  employeeId: number;
  branchId: number;
  leaveType: string;
  startDate: string;
  endDate: string;
  reason?: string;
  notes?: string;
  attachmentPath?: string;
}

export interface UpdateLeaveDto extends CreateLeaveDto {
  leaveId: number;
}

export interface ApproveLeaveDto {
  leaveId: number;
  status: string;
  notes?: string;
}

export interface ReturnToWorkDto {
  leaveId: number;
  actualReturnDate: string;
  notes?: string;
}

export interface BlockedDate {
  date: string;
  reason: string;
  leaveType: string;
  description: string;
}

export const leavesService = {
  getAll: async (branchId?: number, employeeId?: number, status?: string, leaveType?: string): Promise<Leave[]> => {
    const params: Record<string, string> = {};
    if (branchId) params.branchId = branchId.toString();
    if (employeeId) params.employeeId = employeeId.toString();
    if (status) params.status = status;
    if (leaveType) params.leaveType = leaveType;
    const response = await axiosInstance.get('/leaves', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Leave> => {
    const response = await axiosInstance.get(`/leaves/${id}`);
    return response.data;
  },

  getBalance: async (employeeId: number): Promise<LeaveBalance> => {
    const response = await axiosInstance.get(`/leaves/balance/${employeeId}`);
    return response.data;
  },

  getBlockedDates: async (branchId: number, fromDate: string, toDate: string): Promise<Record<number, BlockedDate[]>> => {
    const response = await axiosInstance.get('/leaves/blocked-dates', { params: { branchId, fromDate, toDate } });
    return response.data;
  },

  create: async (dto: CreateLeaveDto): Promise<Leave> => {
    const response = await axiosInstance.post('/leaves', dto);
    return response.data;
  },

  update: async (id: number, dto: UpdateLeaveDto): Promise<Leave> => {
    const response = await axiosInstance.put(`/leaves/${id}`, dto);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/leaves/${id}`);
  },

  acknowledge: async (id: number): Promise<Leave> => {
    const response = await axiosInstance.post(`/leaves/${id}/acknowledge`);
    return response.data;
  },

  approve: async (dto: ApproveLeaveDto): Promise<Leave> => {
    const response = await axiosInstance.post('/leaves/approve', dto);
    return response.data;
  },

  returnToWork: async (dto: ReturnToWorkDto): Promise<Leave> => {
    const response = await axiosInstance.post('/leaves/return-to-work', dto);
    return response.data;
  },
};
